import { Card, Col, Row, Typography } from 'antd'
import { useMemo } from 'react'

import {
  cardState,
  ResourceStatistic,
} from '@/components/common/ResourceStatistic'
import { RefreshablePageHeader } from '@/components/layout/RefreshablePageHeader'
import { formatUsdCents } from '@/utils/money'

import { CapacitySection } from './capacity/CapacitySection'
import { BalanceFlowCard } from './charges/BalanceFlowCard'
import { formatPeriodRange } from './charges/chargePeriods'
import { SCOPE_CHARGED, scopeCharges } from './charges/chargeScope'
import { ChargesSection } from './charges/ChargesSection'
import { chargesByCluster, toCents } from './charges/chargeSummary'
import { BusinessFilters } from './components/BusinessFilters'
import { useBusinessFilters } from './useBusinessFilters'
import {
  BUSINESS_QUERY_KEY,
  useCapacity,
  useCharges,
  useCredited,
} from './useBusinessQueries'

function PeriodNote({ period }: { period?: string }) {
  return period ? (
    <Typography.Text type="secondary">{period}</Typography.Text>
  ) : null
}

export default function BusinessPage() {
  const [filters, setFilters] = useBusinessFilters()
  const { days, window: step, view, scope } = filters
  const charges = useCharges(days, step)
  const scoped = useMemo(
    () => charges.data && scopeCharges(charges.data, scope),
    [charges.data, scope],
  )
  const credited = useCredited(days)
  const capacity = useCapacity()

  const chargedByCluster = useMemo(
    () =>
      scoped
        ? new Map(chargesByCluster(scoped).map((t) => [t.key, t.cents]))
        : undefined,
    [scoped],
  )
  const chargePeriod =
    charges.data && formatPeriodRange(charges.data.from, charges.data.to)

  return (
    <div>
      <RefreshablePageHeader
        title="Capacity & revenue"
        queryKey={BUSINESS_QUERY_KEY}
        queries={[charges, credited, capacity]}
      />

      <BusinessFilters filters={filters} onChange={setFilters} />

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12}>
          <Card>
            <ResourceStatistic
              title="Charged"
              state={cardState({ ...charges, data: scoped })}
              errorText="Failed to load charges"
              value={(data) => formatUsdCents(toCents(data.totalUsd))}
              prefix={null}
            />
            <PeriodNote period={chargePeriod} />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card>
            <ResourceStatistic
              title="Credited, real money, all accounts"
              state={cardState(credited)}
              errorText="Failed to load credited amounts"
              value={(data) => formatUsdCents(data.cents)}
              prefix={null}
            />
            <PeriodNote period={chargePeriod} />
          </Card>
        </Col>
      </Row>

      <BalanceFlowCard
        charges={scoped}
        credited={credited.data?.points}
        loading={charges.isLoading || credited.isLoading}
        step={step}
      />

      <ChargesSection
        charges={scoped}
        scope={scope}
        loading={charges.isLoading}
        days={days}
        step={step}
        view={view}
        onViewChange={(value) => setFilters({ view: value })}
      />

      <CapacitySection
        capacity={capacity.data}
        loading={capacity.isLoading}
        error={capacity.isError}
        chargedByCluster={chargedByCluster}
        chargedLabel={`Charged ${SCOPE_CHARGED[scope]}${chargePeriod ? `, ${chargePeriod}` : ''}`}
      />
    </div>
  )
}
